import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { Rate, RateType } from '@mews-commander/data-access';
import { createOption } from '@mews-commander/ui';
import { Interval } from '@mews-commander/types';
import { useGroupedRatesOptions } from '@mews-commander/legacy-entities/src/queries/rates';
import { SecondaryPriceText } from '../components/SecondaryPriceText';
import { useRatePricingQuery } from '../../../queries/useRatePricingQuery';
import { NewReservationFormKeys, NewReservationFormValues } from '../schema';
import { findRatePriceInRateGroups } from './ratePrices';

interface Params extends Interval {
    serviceId: string;
    resourceCategoryId: string;
    ageCategoryCounterValues: NewReservationFormValues[NewReservationFormKeys.AgeCategories];
}

export const useNewReservationRatesOptionsAndPricing = ({
    serviceId,
    resourceCategoryId,
    startUtc,
    endUtc,
    ageCategoryCounterValues,
}: Params) => {
    const enterpriseId = useCurrentEnterpriseId();

    const ratePricingQuery = useRatePricingQuery({
        serviceId,
        startUtc,
        endUtc,
        categoryOccupancies: [
            {
                categoryId: resourceCategoryId,
                occupancyData: pipe(
                    ageCategoryCounterValues,
                    record.toEntries,
                    array.map(([ageCategoryId, personCount]: [string, number]) => ({ ageCategoryId, personCount }))
                ),
            },
        ],
    });

    const { rateGroups = {} } = ratePricingQuery.data ?? {};

    const ratesOptionsQuery = useGroupedRatesOptions({
        serviceId,
        enterpriseId,
        filterRate: (rate: Rate) =>
            rate.isEnabled &&
            !rate.isDeleted &&
            rate.type === RateType.Public &&
            findRatePriceInRateGroups(rate, rateGroups)?.isRestricted === false,
        convertRateToOption: (rate: Rate) =>
            createOption({
                value: rate.id,
                label: rate.name,
                indent: true,
                secondaryText: (
                    <SecondaryPriceText
                        serviceId={serviceId}
                        price={findRatePriceInRateGroups(rate, rateGroups)?.price?.averagePerFullTimeUnit}
                    />
                ),
            }),
    });

    return {
        rateGroups,
        loading: ratePricingQuery.isFetching || ratesOptionsQuery.isFetching,
        ratesOptions: ratesOptionsQuery.data,
    };
};
